// Per-year, per-region economic value attributable to each labor group.
//
// Source: data/econ-model-outputs-snapshot.csv (Plan A).
//   Output Y ($, real) — rows 52 (US), 53 (China), 54 (RoW), 55 (World)
//   Human cog income share (Human cog wages / Y) — rows 234–237
//   Human phys income share (Human phys wages / Y) — rows 242–245
//   AI income share (AI rentals / Y)  — rows 218–221
//   Robot income share (Robot rentals / Y) — rows 226–229
//
// Each datum gives Output Y in 2025 real dollars by region; multiply by the
// matching income share to get the $ contributed by that labor factor in
// that region. If the spreadsheet changes, re-run
// scripts/downloadEconModelOutputs.ts and update both this file and
// laborMixData.ts.

import { LABOR_MIX_DATA, type LaborMixDatum } from "./laborMixData"

export type LaborMixRegion = "world" | "us" | "china" | "row"

export type RegionalShares = Record<LaborMixRegion, number>

export type LaborEconomicValueDatum = {
  year: number
  outputY: RegionalShares
  humansCogShare: RegionalShares
  humansPhysShare: RegionalShares
  aiCogShare: RegionalShares
  robotPhysShare: RegionalShares
}

export const LABOR_ECONOMIC_VALUE_DATA: LaborEconomicValueDatum[] = [
  {
    year: 2025,
    outputY:        { us: 3.06e13, china: 2.07e13, row: 6.57e13, world: 1.17e14 },
    humansCogShare: { us: 0.366253737, china: 0.246840156, row: 0.359018357, world: 0.354261186 },
    humansPhysShare:{ us: 0.178625101, china: 0.244620007, row: 0.23966647,  world: 0.238299266 },
    aiCogShare:     { us: 0.021346263, china: 0.003159844, row: 0.000981643, world: 0.005738814 },
    robotPhysShare: { us: 0.003774899, china: 0.005379993, row: 0.00033353,  world: 0.001700734 },
  },
  {
    year: 2026,
    outputY:        { us: 3.10e13, china: 2.07e13, row: 6.26e13, world: 1.13e14 },
    humansCogShare: { us: 0.354889435, china: 0.241792237, row: 0.357374157, world: 0.345189723 },
    humansPhysShare:{ us: 0.177633996, china: 0.243133994, row: 0.23956552,  world: 0.237827015 },
    aiCogShare:     { us: 0.032710565, china: 0.008207763, row: 0.002625843, world: 0.014810277 },
    robotPhysShare: { us: 0.004766004, china: 0.006866006, row: 0.00043448,  world: 0.002172985 },
  },
  {
    year: 2027,
    outputY:        { us: 3.22e13, china: 2.10e13, row: 6.11e13, world: 1.12e14 },
    humansCogShare: { us: 0.347752952, china: 0.232031759, row: 0.354124058, world: 0.328001083 },
    humansPhysShare:{ us: 0.176554468, china: 0.241440914, row: 0.239458077, world: 0.2372941   },
    aiCogShare:     { us: 0.039847048, china: 0.017968241, row: 0.005875942, world: 0.031998917 },
    robotPhysShare: { us: 0.005845532, china: 0.008559086, row: 0.000541923, world: 0.0027059   },
  },
  {
    year: 2028,
    outputY:        { us: 3.38e13, china: 2.13e13, row: 6.00e13, world: 1.13e14 },
    humansCogShare: { us: 0.341234251, china: 0.215821648, row: 0.348358093, world: 0.300370381 },
    humansPhysShare:{ us: 0.175284323, china: 0.239380945, row: 0.239327068, world: 0.236651817 },
    aiCogShare:     { us: 0.046365749, china: 0.034178352, row: 0.011641907, world: 0.059629619 },
    robotPhysShare: { us: 0.007115677, china: 0.010619055, row: 0.000672932, world: 0.003348183 },
  },
  {
    year: 2029,
    outputY:        { us: 3.57e13, china: 2.17e13, row: 5.88e13, world: 1.13e14 },
    humansCogShare: { us: 0.331227373, china: 0.20021842,  row: 0.342295398, world: 0.274773121 },
    humansPhysShare:{ us: 0.173518736, china: 0.236505647, row: 0.239140852, world: 0.235746215 },
    aiCogShare:     { us: 0.056372627, china: 0.04978158,  row: 0.017704602, world: 0.085226879 },
    robotPhysShare: { us: 0.008881264, china: 0.013494353, row: 0.000859148, world: 0.004253785 },
  },
  {
    year: 2030,
    outputY:        { us: 3.92e13, china: 2.22e13, row: 5.66e13, world: 1.18e14 },
    humansCogShare: { us: 0.321984423, china: 0.176148717, row: 0.320087997, world: 0.257318355 },
    humansPhysShare:{ us: 0.170835182, china: 0.231991867, row: 0.238836405, world: 0.234335087 },
    aiCogShare:     { us: 0.065615577, china: 0.073851283, row: 0.039912003, world: 0.102681645 },
    robotPhysShare: { us: 0.011564818, china: 0.018008133, row: 0.001163595, world: 0.005664913 },
  },
  {
    year: 2031,
    outputY:        { us: 4.51e13, china: 2.23e13, row: 5.36e13, world: 1.19e14 },
    humansCogShare: { us: 0.290757714, china: 0.149115119, row: 0.302381802, world: 0.218658161 },
    humansPhysShare:{ us: 0.163424039, china: 0.219517029, row: 0.237954583, world: 0.230248575 },
    aiCogShare:     { us: 0.096842286, china: 0.100884881, row: 0.057618198, world: 0.141341839 },
    robotPhysShare: { us: 0.018975961, china: 0.030482971, row: 0.002045417, world: 0.009751425 },
  },
  {
    year: 2032,
    outputY:        { us: 6.70e13, china: 2.67e13, row: 5.81e13, world: 1.50e14 },
    humansCogShare: { us: 0.25253396,  china: 0.120861349, row: 0.222458869, world: 0.17754941  },
    humansPhysShare:{ us: 0.147924136, china: 0.192906574, row: 0.235751422, world: 0.220859477 },
    aiCogShare:     { us: 0.13506604,  china: 0.129138651, row: 0.137541131, world: 0.18245059  },
    robotPhysShare: { us: 0.034475864, china: 0.057093426, row: 0.004248578, world: 0.019140523 },
  },
  {
    year: 2033,
    outputY:        { us: 1.37e14, china: 4.32e13, row: 9.75e13, world: 2.52e14 },
    humansCogShare: { us: 0.193989158, china: 0.096503563, row: 0.106661006, world: 0.129907862 },
    humansPhysShare:{ us: 0.089081342, china: 0.16920208,  row: 0.222683169, world: 0.19836276  },
    aiCogShare:     { us: 0.193610842, china: 0.153496437, row: 0.253338994, world: 0.230092138 },
    robotPhysShare: { us: 0.093318658, china: 0.08079792,  row: 0.017316831, world: 0.04163724  },
  },
  {
    year: 2034,
    outputY:        { us: 3.90e14, china: 8.11e13, row: 1.86e14, world: 5.21e14 },
    humansCogShare: { us: 0.104111516, china: 0.049119781, row: 0.050233019, world: 0.063198246 },
    humansPhysShare:{ us: 0.046640077, china: 0.120054547, row: 0.195867475, world: 0.155215883 },
    aiCogShare:     { us: 0.283488484, china: 0.200880219, row: 0.309766981, world: 0.296801754 },
    robotPhysShare: { us: 0.135759923, china: 0.129945453, row: 0.044132525, world: 0.084784117 },
  },
  {
    year: 2035,
    outputY:        { us: 1.00e15, china: 1.43e14, row: 3.06e14, world: 9.45e14 },
    humansCogShare: { us: 0.020549843, china: 0.009105912, row: 0.009893276, world: 0.011226994 },
    humansPhysShare:{ us: 0.022286077, china: 0.074756662, row: 0.154974212, world: 0.105227413 },
    aiCogShare:     { us: 0.367050157, china: 0.240894088, row: 0.350106724, world: 0.348773006 },
    robotPhysShare: { us: 0.160113923, china: 0.175243338, row: 0.085025788, world: 0.134772587 },
  },
  {
    year: 2036,
    outputY:        { us: 2.05e15, china: 2.36e14, row: 4.43e14, world: 1.51e15 },
    humansCogShare: { us: 0.017105351, china: 0.007220219, row: 0.006827326, world: 0.008844515 },
    humansPhysShare:{ us: 0.008659665, china: 0.031153854, row: 0.098582997, world: 0.0516114   },
    aiCogShare:     { us: 0.370494649, china: 0.242779781, row: 0.353172674, world: 0.351155485 },
    robotPhysShare: { us: 0.173740335, china: 0.218846146, row: 0.141417003, world: 0.1883886   },
  },
  {
    year: 2037,
    outputY:        { us: 3.57e15, china: 3.82e14, row: 6.19e14, world: 2.32e15 },
    humansCogShare: { us: 0.014599664, china: 0.005629373, row: 0.005457559, world: 0.006847485 },
    humansPhysShare:{ us: 0.009959484, china: 0.009933775, row: 0.045323467, world: 0.018454517 },
    aiCogShare:     { us: 0.373000336, china: 0.244370627, row: 0.354542441, world: 0.353152515 },
    robotPhysShare: { us: 0.172440516, china: 0.240066225, row: 0.194676533, world: 0.221545483 },
  },
  {
    year: 2038,
    outputY:        { us: 5.57e15, china: 6.16e14, row: 8.72e14, world: 3.50e15 },
    humansCogShare: { us: 0.013802115, china: 0.00438237,  row: 0.004609909, world: 0.005386898 },
    humansPhysShare:{ us: 0.013029061, china: 0.009749856, row: 0.015491031, world: 0.008255906 },
    aiCogShare:     { us: 0.373797885, china: 0.24561763,  row: 0.355390091, world: 0.354613102 },
    robotPhysShare: { us: 0.169370939, china: 0.240250144, row: 0.224508969, world: 0.231744094 },
  },
  {
    year: 2039,
    outputY:        { us: 8.16e15, china: 9.95e14, row: 1.25e15, world: 5.27e15 },
    humansCogShare: { us: 0.012557438, china: 0.003063964, row: 0.004124192, world: 0.003715407 },
    humansPhysShare:{ us: 0.018497879, china: 0.010905535, row: 0.007369988, world: 0.009124769 },
    aiCogShare:     { us: 0.375042562, china: 0.246936036, row: 0.355875808, world: 0.356284593 },
    robotPhysShare: { us: 0.163902121, china: 0.239094465, row: 0.232630012, world: 0.230875231 },
  },
  {
    year: 2040,
    outputY:        { us: 1.18e16, china: 1.62e15, row: 1.86e15, world: 8.04e15 },
    humansCogShare: { us: 0.007983912, china: 0.00140225,  row: 0.00379546,  world: 0.001637684 },
    humansPhysShare:{ us: 0.027135026, china: 0.012133127, row: 0.007627452, world: 0.009806517 },
    aiCogShare:     { us: 0.379616088, china: 0.24859775,  row: 0.35620454,  world: 0.358362316 },
    robotPhysShare: { us: 0.155264974, china: 0.237866873, row: 0.232372548, world: 0.230193483 },
  },
]

// Plan A / World H100e chip stock — row 4 of econ-model-outputs-snapshot.csv.
// Used by computeDerivedAgents to convert a year into a compute level and
// then derive copies/speed/rdMultiple.
export const TOTAL_COMPUTE_H100E_BY_YEAR: Record<number, number> = {
  2025: 8.5e6,
  2026: 2.0e7,
  2027: 4.84e7,
  2028: 1.16e8,
  2029: 2.4e8,
  2030: 4.0e8,
  2031: 9.68e8,
  2032: 3.26e9,
  2033: 1.22e10,
  2034: 4.12e10,
  2035: 1.0e11,
  2036: 1.79e11,
  2037: 2.88e11,
  2038: 4.36e11,
  2039: 6.5e11,
  2040: 1.0e12,
}

export const getEconomicValueDatumForYear = (
  year: number,
): LaborEconomicValueDatum => {
  const exact = LABOR_ECONOMIC_VALUE_DATA.find((datum) => datum.year === year)
  if (exact) return exact
  const minYear = LABOR_ECONOMIC_VALUE_DATA[0].year
  const maxYear = LABOR_ECONOMIC_VALUE_DATA[LABOR_ECONOMIC_VALUE_DATA.length - 1].year
  const clampedYear = Math.max(minYear, Math.min(maxYear, year))
  return (
    LABOR_ECONOMIC_VALUE_DATA.find((datum) => datum.year === clampedYear) ??
    LABOR_ECONOMIC_VALUE_DATA[0]
  )
}

export const getComputeForYear = (year: number): number => {
  const exact = TOTAL_COMPUTE_H100E_BY_YEAR[year]
  if (exact !== undefined) return exact
  const minYear = LABOR_ECONOMIC_VALUE_DATA[0].year
  const maxYear = LABOR_ECONOMIC_VALUE_DATA[LABOR_ECONOMIC_VALUE_DATA.length - 1].year
  const clampedYear = Math.max(minYear, Math.min(maxYear, year))
  return TOTAL_COMPUTE_H100E_BY_YEAR[clampedYear] ?? TOTAL_COMPUTE_H100E_BY_YEAR[minYear]
}

// Re-exported so callers don't need to import laborMixData.ts directly when
// they want to fall back to HE counts; lets the two snapshot files stay
// addressable from a single import surface.
export type { LaborMixDatum }
export { LABOR_MIX_DATA }
